// Catalog.ID Client — Key Grant Envelope Signature Verification
// ============================================================================
//
// Module-identity signing (roadmap §5.4): every inbound key-grant-request must
// carry an Ed25519 signature over a canonical payload, signed with the
// requester's registered pubkey. The hub already gates which connections can
// originate routed RPC traffic by role; this is the second line — payload-
// level — that protects against a registered-but-misbehaving relay.
//
// We use @noble/curves' Ed25519 (already in the cld-id client's dep tree via
// noble/post-quantum + noble/curves) rather than WebCrypto's Ed25519 because
// browser support is uneven (Firefox shipped Ed25519 only recently; Safari
// requires 17+). noble runs everywhere and is constant-time.
//
// Canonical payload format MUST match every sender's copy — today
// `apps/efs/src/client-service/BitcashGrant/KeyGrantCanonicalSigning.cs` and
// `apps/cpr/src/client-service/Crypto/BitcashGrant/KeyGrantCanonicalSigning.cs`:
//   <type>|<to>|<requestId>|<requesterId>|<mode>|<keyId>|<keyIndex>|<ephemeralPubkey>|<expectedCid>
// (no trailing pipe)
//
// `expectedCid` (2026-10-08) is the account the module works for — every
// key-grant mode is account-scoped (see `account-gate.ts`), so it is always the
// last committed field, and a hop that rewrote it fails verification here.
//
// An optional save-mode suffix — the `encryptedBundle` triple — used to be
// appended when the field was present. cid-encrypted-storage M6b retired the
// `save` mode and the field with it, and dropping the branch moved no bytes:
// it only ever ran for an envelope that carried a bundle, and no surviving
// sender builds one. Both C# copies above already omit it.
// ============================================================================

import { ed25519 } from '@noble/curves/ed25519.js';
import type { KeyGrantRequest } from './types';

function b64ToBytes(s: string): Uint8Array {
    const bin = atob(s);
    const out = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
    return out;
}

export function buildSignedPayload(env: KeyGrantRequest): Uint8Array {
    const parts = [
        env.type,
        env.to,
        env.requestId,
        env.requesterId,
        env.mode,
        env.keyId,
        String(env.keyIndex ?? 0),
        env.ephemeralPubkey ?? '',
        env.expectedCid ?? '',
    ];
    return new TextEncoder().encode(parts.join('|'));
}

/**
 * Verify the request's signature against the supplied (registered) pubkey.
 * Returns false if any field is missing, the pubkey doesn't decode, or the
 * signature doesn't verify. Never throws — caller treats false as "reject".
 */
export function verifyRequest(env: KeyGrantRequest, registeredPubkeyB64: string): boolean {
    if (!env.signature || !env.signerPubkey) return false;
    // The signerPubkey claimed in the envelope must match the one the user
    // registered. This is what binds requesterId to a specific binary's key.
    if (env.signerPubkey !== registeredPubkeyB64) return false;
    let pub: Uint8Array;
    let sig: Uint8Array;
    try {
        pub = b64ToBytes(registeredPubkeyB64);
        sig = b64ToBytes(env.signature);
    } catch { return false; }
    if (pub.length !== 32 || sig.length !== 64) return false;
    try {
        return ed25519.verify(sig, buildSignedPayload(env), pub);
    } catch { return false; }
}
